import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import { PageShell } from './components/PageShell/PageShell';
import { BookmarkPageContainer } from './pages/Bookmarks/pages/BookmarkPageContainer';
import { CareerPathPageContainer } from './pages/CareerPath/CareerPathPageContainer';
import { CareerTrackPageContainer } from './pages/CareerTrack/CareerTrackPageContainer';
import { CollegePageContainer } from './pages/College/CollegePageContainer';
import { ComingSoonPage } from './pages/ComingSoon/ComingSoonPage';
import { CompetencyPageContainer } from './pages/Competency/pages/CompetencyPageContainer';
import { EmployerPageContainer } from './pages/Employer/EmployerPageContainer';
import { GeneratingBlueprintPageContainer } from './pages/Generating/GeneratingBlueprintPageContainer';
import { HomePageContainer } from './pages/Home/HomePageContainer';
import { IntroSlidesPageContainer } from './pages/Intro/IntroSlidesPageContainer';
import { LifeReadinessPageContainer } from './pages/LifeReadiness/LifeReadinessPageContainer';
import { LoginPageContainer } from './pages/Login/LoginPageContainer';
import { OneTimePasswordContainer } from './pages/Login/OneTimePasswordContainer';
import { MarkersOfSuccessPageContainer } from './pages/MarkersOfSuccess/MarkersOfSuccessPageContainer';
import { NotFound404Page } from './pages/NotFound404/NotFound404Page';
import { OfficeHours } from './pages/OfficeHours/OfficeHoursPage';
import { OnboardingPageContainer } from './pages/Onboarding/OnboardingPageContainer';
import { ProfilePageContainer } from './pages/Profile/ProfilePageContainer';
import { ProsperPageContainer } from './pages/Prosper/pages/ProsperPageContainer';
import { RegistrationPage } from './pages/Register/RegistrationPage';
import { MyResultsPageContainer } from './pages/Result/MyResultsPageContainer';
import { SearchPageContainer } from './pages/Search/pages/SearchPageContainer';
import { SettingsPageContainer } from './pages/Settings/SettingsPageContainer';
import { SolutionSession } from './pages/SolutionSession/SolutionSessionPage';
import { SurveyPageContainer } from './pages/Survey/SurveyPageContainer';
import { PostHogPageViewShell } from './providers/PostHogPageViewShell';
import { ProtectedRoute } from './providers/ProtectedRoute';

export type LegacyPageTypes = 'BLUEPRINT' | 'PROSPER' | 'LUBAV' | 'UNAUTHENTICATED' | 'BACKRS_4.0';

export const APP_ROUTE = {
  // COMMON
  LOGIN: '/login',
  OTP: '/login/verify',
  INTRO: '/intro',
  ONBOARDING: '/onboarding',

  // GROW
  HOME: '/',
  UNIVERSITY: '/university',
  EMPLOYER: '/employer',
  SEARCH: '/search',
  LIFE_READINESS: '/life-readiness',
  LIFE_READINESS_QUIZ: '/life-readiness/:categoryId/:subcategoryId',
  PROFILE: '/profile',
  SETTINGS: '/settings',

  // BLUEPRINT
  CAREER_PATHS: '/career-paths',
  CAREER_TRACKS: '/career-tracks',
  SURVEY: '/survey',
  GENERATING_BLUEPRINT: '/generating-blueprint',
  MY_BLUEPRINT: '/my-blueprint',
  MARKERS_OF_SUCCESS: '/markers-of-success',

  // PROSPER
  PROSPER: '/prosper',
  COMPETENCY: '/competency',
  BOOKMARKS: '/bookmarks',

  // LUBAV
  REGISTER: '/register',
  OFFICE_HOURS: '/office-hours/:id?',
  OFFICE_HOURS_SUPERUSER: '/superuser',
  OFFICE_HOURS_SUPERCOACH: '/supercoach',
  SOLUTION_SESSION: '/solution-session/:id?',

  // ALL OTHERS (404)
  NOT_FOUND: '*',
  NOT_FOUND_404: '/404',
  COMING_SOON: '/coming-soon',
} as const;

type AppRoute = Readonly<{
  protected: boolean;
  path: (typeof APP_ROUTE)[keyof typeof APP_ROUTE];
  page: JSX.Element;
  queryParam?: string[];
  legacyPageType?: LegacyPageTypes;
  subRoute?: string;
}>;

export const APP_ROUTES: Record<keyof typeof APP_ROUTE, AppRoute> = {
  HOME: {
    protected: true,
    path: APP_ROUTE.HOME,
    page: <HomePageContainer />,
    legacyPageType: 'BACKRS_4.0',
  },
  LOGIN: {
    protected: false,
    path: APP_ROUTE.LOGIN,
    page: <LoginPageContainer />,
    legacyPageType: 'UNAUTHENTICATED',
  },
  OTP: {
    protected: false,
    path: APP_ROUTE.OTP,
    page: <OneTimePasswordContainer />,
    legacyPageType: 'UNAUTHENTICATED',
  },
  REGISTER: {
    protected: false,
    path: APP_ROUTE.REGISTER,
    page: <RegistrationPage />,
    legacyPageType: 'UNAUTHENTICATED',
  },
  ONBOARDING: {
    protected: false,
    path: APP_ROUTE.ONBOARDING,
    page: <OnboardingPageContainer />,
    legacyPageType: 'UNAUTHENTICATED',
    subRoute: 'onboardingType',
  },

  UNIVERSITY: {
    protected: true,
    path: APP_ROUTE.UNIVERSITY,
    page: <CollegePageContainer />,
    legacyPageType: 'BACKRS_4.0',
    subRoute: 'universityId',
  },

  EMPLOYER: {
    protected: true,
    path: APP_ROUTE.EMPLOYER,
    page: <EmployerPageContainer />,
    legacyPageType: 'BACKRS_4.0',
    subRoute: 'employerSlug',
  },

  LIFE_READINESS: {
    protected: true,
    path: APP_ROUTE.LIFE_READINESS, // "/life-readiness"
    page: <LifeReadinessPageContainer />,
    legacyPageType: 'BACKRS_4.0',
    subRoute: 'categoryId',
  },

  LIFE_READINESS_QUIZ: {
    protected: true,
    path: APP_ROUTE.LIFE_READINESS_QUIZ, // "/life-readiness/:categoryId/:subcategoryId"
    page: <LifeReadinessPageContainer />,
    legacyPageType: 'BACKRS_4.0',
  },

  PROFILE: {
    protected: true,
    path: APP_ROUTE.PROFILE,
    page: <ProfilePageContainer />,
    legacyPageType: 'BACKRS_4.0',
    subRoute: 'profile',
  },

  SETTINGS: {
    protected: true,
    path: APP_ROUTE.SETTINGS,
    page: <SettingsPageContainer />,
    legacyPageType: 'BACKRS_4.0',
  },

  CAREER_PATHS: {
    protected: true,
    path: APP_ROUTE.CAREER_PATHS,
    page: <CareerPathPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },
  CAREER_TRACKS: {
    protected: true,
    path: APP_ROUTE.CAREER_TRACKS,
    page: <CareerTrackPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },
  SURVEY: {
    protected: false,
    path: APP_ROUTE.SURVEY,
    page: <SurveyPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },
  GENERATING_BLUEPRINT: {
    protected: false,
    path: APP_ROUTE.GENERATING_BLUEPRINT,
    page: <GeneratingBlueprintPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },
  MY_BLUEPRINT: {
    protected: false,
    path: APP_ROUTE.MY_BLUEPRINT,
    page: <MyResultsPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },
  INTRO: {
    protected: false,
    path: APP_ROUTE.INTRO,
    page: <IntroSlidesPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },
  MARKERS_OF_SUCCESS: {
    protected: true,
    path: APP_ROUTE.MARKERS_OF_SUCCESS,
    page: <MarkersOfSuccessPageContainer />,
    legacyPageType: 'BLUEPRINT',
  },

  PROSPER: {
    protected: true,
    path: APP_ROUTE.PROSPER,
    page: <ProsperPageContainer />,
    legacyPageType: 'PROSPER',
  },
  COMPETENCY: {
    protected: true,
    path: APP_ROUTE.COMPETENCY,
    queryParam: ['slug'],
    page: <CompetencyPageContainer />,
    legacyPageType: 'PROSPER',
  },
  SEARCH: {
    protected: true,
    path: APP_ROUTE.SEARCH,
    page: <SearchPageContainer />,
    legacyPageType: 'BACKRS_4.0',
    subRoute: 'subPageCategory',
  },
  BOOKMARKS: {
    protected: true,
    path: APP_ROUTE.BOOKMARKS,
    page: <BookmarkPageContainer />,
    legacyPageType: 'PROSPER',
  },

  OFFICE_HOURS: {
    protected: true,
    path: APP_ROUTE.OFFICE_HOURS,
    page: <OfficeHours superuser={false} supercoach={false} />,
    legacyPageType: 'LUBAV',
  },
  OFFICE_HOURS_SUPERUSER: {
    protected: true,
    path: APP_ROUTE.OFFICE_HOURS_SUPERUSER,
    page: <OfficeHours superuser={true} supercoach={false} />,
    legacyPageType: 'LUBAV',
  },
  OFFICE_HOURS_SUPERCOACH: {
    protected: true,
    path: APP_ROUTE.OFFICE_HOURS_SUPERCOACH,
    page: <OfficeHours superuser={false} supercoach={true} />,
    legacyPageType: 'LUBAV',
  },
  SOLUTION_SESSION: {
    protected: true,
    path: APP_ROUTE.SOLUTION_SESSION,
    page: <SolutionSession />,
    legacyPageType: 'LUBAV',
  },

  // ALL OTHERS (404)
  NOT_FOUND: {
    protected: false,
    path: '*',
    page: <NotFound404Page />,
    legacyPageType: 'UNAUTHENTICATED',
  },
  NOT_FOUND_404: {
    protected: false,
    path: APP_ROUTE.NOT_FOUND_404,
    page: <NotFound404Page />,
    legacyPageType: 'UNAUTHENTICATED',
  },
  COMING_SOON: {
    protected: true,
    path: APP_ROUTE.COMING_SOON,
    page: <ComingSoonPage />,
    legacyPageType: 'BACKRS_4.0',
  },
};

export const App = () => {
  return (
    <Router>
      <PostHogPageViewShell>
        <Routes>
          <Route element={<PageShell />}>
            {Object.values(APP_ROUTES).map((route) => {
              const path = route.queryParam?.length
                ? `${route.path}/:${route.queryParam.join('/:')}`
                : route.path;

              const baseElement = route.protected ? (
                <ProtectedRoute>{route.page}</ProtectedRoute>
              ) : (
                route.page
              );

              return (
                <Route key={route.path} path={path} element={baseElement}>
                  {route.subRoute && <Route path={`:${route.subRoute}`} element={baseElement} />}
                </Route>
              );
            })}
          </Route>
        </Routes>
      </PostHogPageViewShell>
    </Router>
  );
};
